import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuth,
  workspaceService,
  TeamWorkspaceCreateRequest,
  Button,
  Input,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@aaas/shared';

export function WorkspaceCreatePage() {
  const navigate = useNavigate();
  const { user, userProfile, refreshWorkspaces, switchWorkspace } = useAuth();
  const { t } = useTranslation('portal');

  const [workspaceCode, setWorkspaceCode] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  // Check workspace code availability
  const checkCodeAvailability = async (code: string) => {
    if (!code || code.length < 3) {
      setCodeAvailable(null);
      setCodeError(null);
      return;
    }

    setIsCheckingCode(true);
    setCodeError(null);

    try {
      const result = await workspaceService.checkWorkspaceCode(code);
      setCodeAvailable(result.available);
      if (!result.available) {
        setCodeError(result.message || 'Code is not available');
      }
    } catch (error) {
      console.error('Error checking code:', error);
      setCodeError('Failed to check code availability');
      setCodeAvailable(null);
    } finally {
      setIsCheckingCode(false);
    }
  };

  // Debounce code checking
  useEffect(() => {
    const timer = setTimeout(() => {
      checkCodeAvailability(workspaceCode);
    }, 500);

    return () => clearTimeout(timer);
  }, [workspaceCode]);

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !workspaceCode || !workspaceName) {
      setError(t('workspaceCreate.errors.fillAllFields'));
      return;
    }

    if (codeAvailable === false) {
      setError(t('workspaceCreate.errors.chooseAvailableCode'));
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const idToken = await user.getIdToken();
      const workspaceData: TeamWorkspaceCreateRequest = {
        code: workspaceCode.trim().toLowerCase(),
        name: workspaceName.trim(),
      };

      const result = await workspaceService.createTeamWorkspace(workspaceData, idToken);

      // If we get a workspace object back, it means success
      if (result && result.id) {
        // Refresh workspaces to get the new workspace info
        await refreshWorkspaces();
        // Auto-switch to the newly created workspace
        await switchWorkspace(result.id);
        // Navigate back to the main app
        navigate('/', { replace: true });
      } else {
        setError(t('workspaceCreate.errors.createFailed'));
      }
    } catch (error: unknown) {
      console.error('Workspace creation failed:', error);
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string; message?: string } } };
        setError(
          axiosError.response?.data?.message ||
            axiosError.response?.data?.detail ||
            t('workspaceCreate.errors.createFailed'),
        );
      } else {
        setError(t('workspaceCreate.errors.createFailed'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const formatWorkspaceCode = (value: string) => {
    // Only allow alphanumeric characters and hyphens, convert to lowercase
    return value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCode = formatWorkspaceCode(e.target.value);
    setWorkspaceCode(formattedCode);
  };

  const getCodeInputStatus = () => {
    if (!workspaceCode || workspaceCode.length < 3) return 'default';
    if (isCheckingCode) return 'checking';
    if (codeAvailable === true) return 'available';
    if (codeAvailable === false) return 'unavailable';
    return 'default';
  };

  const getCodeInputBorderClass = () => {
    const status = getCodeInputStatus();
    switch (status) {
      case 'available':
        return 'border-green-500 focus-visible:ring-green-500';
      case 'unavailable':
        return 'border-red-500 focus-visible:ring-red-500';
      case 'checking':
        return 'border-yellow-500 focus-visible:ring-yellow-500';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('workspaceCreate.heading')}</h1>
          <p className="mt-2 text-gray-600">{t('workspaceCreate.subheading')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('workspaceCreate.cardTitle')}</CardTitle>
            <CardDescription>{t('workspaceCreate.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateWorkspace} className="space-y-6">
              <div>
                <label htmlFor="workspaceCode" className="block text-sm font-medium text-gray-700">
                  {t('workspaceCreate.codeLabel')}
                </label>
                <Input
                  id="workspaceCode"
                  type="text"
                  value={workspaceCode}
                  onChange={handleCodeChange}
                  placeholder={t('workspaceCreate.codePlaceholder')}
                  className={`mt-1 ${getCodeInputBorderClass()}`}
                  minLength={3}
                  maxLength={50}
                  required
                  disabled={isCreating}
                />
                <div className="mt-1 min-h-[20px]">
                  {workspaceCode.length > 0 && workspaceCode.length < 3 && (
                    <p className="text-sm text-gray-500">{t('workspaceCreate.codeHint')}</p>
                  )}
                  {isCheckingCode && (
                    <p className="text-sm text-yellow-600">{t('workspaceCreate.checkingAvailability')}</p>
                  )}
                  {codeAvailable === true && workspaceCode.length >= 3 && (
                    <p className="text-sm text-green-600">{t('workspaceCreate.codeAvailable')}</p>
                  )}
                  {codeError && <p className="text-sm text-red-600">{codeError}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="workspaceName" className="block text-sm font-medium text-gray-700">
                  {t('workspaceCreate.nameLabel')}
                </label>
                <Input
                  id="workspaceName"
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder={t('workspaceCreate.namePlaceholder')}
                  className="mt-1"
                  maxLength={100}
                  required
                  disabled={isCreating}
                />
                <p className="mt-1 text-sm text-gray-500">{t('workspaceCreate.nameHint')}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/')}
                  disabled={isCreating}
                >
                  {t('workspaceCreate.cancelButton')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={
                    isCreating ||
                    !workspaceCode ||
                    !workspaceName ||
                    workspaceCode.length < 3 ||
                    codeAvailable === false ||
                    isCheckingCode
                  }
                >
                  {isCreating ? t('workspaceCreate.creatingButton') : t('workspaceCreate.createButton')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            {t('workspaceCreate.userLabel', {
              name: userProfile?.display_name || userProfile?.email || 'Unknown',
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
